import React, { Fragment, useState, forwardRef, useRef, useEffect } from 'react';
import { DateRangePicker } from 'react-date-range';
import { Transition, Portal } from '@headlessui/react';
import moment from 'moment';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { cn } from '@/utils';
import BaseInput from '../input';

type DateRange = {
  start: string;
  end: string;
};

type BaseDateRangePickerPopOverProps = {
  label?: string;
  value: DateRange;
  setValue: (value: DateRange) => void;
  className?: string;
  classNameContainer?: string;
};

const BaseDateRangePickerPopOver = forwardRef<HTMLDivElement, BaseDateRangePickerPopOverProps>(
  ({ label, value, setValue, className, classNameContainer }, ref) => {
    const [state, setState] = useState([
      {
        startDate: value?.start ? new Date(value?.start) : new Date(),
        endDate: value?.end ? new Date(value?.end) : new Date(),
        key: 'selection',
      },
    ]);

    const containerRef = useRef<HTMLDivElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [coords, setCoords] = useState({ top: 0, left: 0 });
    const [focusedRange, setFocusedRange] = useState<[number, 0 | 1]>([0, 0]);

    const checkPosition = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const popoverWidth = window.innerWidth < 640 ? 330 : 560;
      const popoverHeight = 350;
      
      let top = rect.bottom + 8;
      let left = rect.left;

      if (rect.bottom + popoverHeight > window.innerHeight && rect.top > popoverHeight + 8) {
        top = rect.top - popoverHeight - 8;
      }
      
      if (left + popoverWidth > window.innerWidth) {
        left = window.innerWidth - popoverWidth - 16;
      }
      
      if (left < 16) {
        left = 16;
      }
      
      setCoords({ top, left });
    };

    // Calculate position when opened or resized or scrolled
    useEffect(() => {
      if (isOpen) {
        checkPosition();
        window.addEventListener('resize', checkPosition);
        window.addEventListener('scroll', checkPosition, true);
        return () => {
          window.removeEventListener('resize', checkPosition);
          window.removeEventListener('scroll', checkPosition, true);
        };
      }
    }, [isOpen]);

    // Handle outside click using mousedown across BOTH container and portaled panel
    useEffect(() => {
      if (!isOpen) return;
      const handleOutsideClick = (e: MouseEvent | TouchEvent) => {
        const isClickInContainer = containerRef.current && containerRef.current.contains(e.target as Node);
        const isClickInPanel = panelRef.current && panelRef.current.contains(e.target as Node);

        if (!isClickInContainer && !isClickInPanel) {
          setIsOpen(false);
        }
      };

      document.addEventListener('mousedown', handleOutsideClick);
      document.addEventListener('touchstart', handleOutsideClick);

      return () => {
        document.removeEventListener('mousedown', handleOutsideClick);
        document.removeEventListener('touchstart', handleOutsideClick);
      };
    }, [isOpen]);

    return (
      <div className="relative w-full" ref={containerRef}>
        <div 
          className="w-full cursor-pointer" 
          onClick={() => {
            if (!isOpen) {
              setState([
                {
                  startDate: value?.start ? new Date(value.start) : new Date(),
                  endDate: value?.end ? new Date(value.end) : new Date(),
                  key: 'selection',
                },
              ]);
              setFocusedRange([0, 0]);
            }
            setIsOpen(!isOpen);
          }}
        >
          {label && <label className="form-label font-medium dark:text-white cursor-pointer">{label}</label>}
          <div className="pointer-events-none">
            <BaseInput
              className={clsx(
                'w-full py-3 px-3 h-auto bg-transparent dark:bg-zinc-900 dark:text-slate-200 text-sm text-gray-400 cursor-pointer',
                className,
              )}
              readOnly
              placeholder={`Input ${label}`}
              value={`${moment(state[0].startDate).format('YYYY-MM-DD')} - ${moment(
                state[0].endDate,
              ).format('YYYY-MM-DD')}`}
            />
          </div>
        </div>
        
        <Portal>
          <Transition
            show={isOpen}
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <div
              ref={panelRef}
              className={clsx(
                'fixed bg-white dark:bg-zinc-900 z-[99999] w-fit h-fit shadow-2xl rounded-2xl border border-gray-100 dark:border-gray-800',
                classNameContainer,
              )}
              style={{
                top: `${coords.top}px`,
                left: `${coords.left}px`,
              }}
            >
              <DateRangePicker
                className={cn('p-2 bg-white dark:bg-zinc-900 rounded-2xl z-10')}
                editableDateInputs
                focusedRange={focusedRange}
                onRangeFocusChange={(range: any) => {
                  setFocusedRange(range as [number, 0 | 1]);
                }}
                onChange={(item: any) => {
                  const startDate = moment(item.selection.startDate);
                  const endDate = moment(item.selection.endDate);
                  if (endDate.diff(startDate, 'days') <= 30) {
                    setState([
                      {
                        startDate: item.selection.startDate,
                        endDate: item.selection.endDate,
                        key: item.selection.key || 'selection',
                      },
                    ]);

                    if (focusedRange[1] === 1) {
                      setValue({
                        start: startDate.format('YYYY-MM-DD HH:mm:ss'),
                        end: endDate.endOf('day').format('YYYY-MM-DD HH:mm:ss'),
                      });
                      setIsOpen(false);
                    }
                  } else {
                    toast.error('Only 30 days range allowed', {
                      position: 'top-center',
                    });
                  }
                }}
                moveRangeOnFirstSelection={false}
                ranges={state}
                rangeColors={['#0096FB']}
                color='#0096FB'
              />
            </div>
          </Transition>
        </Portal>
      </div>
    );
  },
);

BaseDateRangePickerPopOver.displayName = 'BaseDateRangePickerPopOver';
export default BaseDateRangePickerPopOver;
